const isInsideFixedLayer = (node: HTMLElement) => {
  for (let el: HTMLElement | null = node; el; el = el.parentElement) {
    if (window.getComputedStyle(el).position === "fixed") return true;
  }
  return false;
};

const isInInactiveScreen = (node: Element) => {
  const start = node instanceof HTMLElement ? node : node.parentElement;
  for (let el: HTMLElement | null = start; el; el = el.parentElement) {
    if (el.hidden || el.getAttribute("aria-hidden") === "true") return true;
    if (el.style.display === "none") return true;
    const style = window.getComputedStyle(el);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.contentVisibility === "hidden"
    ) {
      return true;
    }
  }
  return false;
};

// A blurred stack screen stays mounted. Its height watcher must not publish
// over the screen the user is looking at.
export const isShownScreen = (element: HTMLElement) => {
  if (!element.isConnected || isInInactiveScreen(element)) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 1 && rect.height > 1 && rect.bottom > 0 && rect.right > 0;
};

const inFlowChildren = (element: HTMLElement) =>
  Array.from(element.children).filter((child): child is HTMLElement => {
    if (!(child instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(child);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.position === "fixed" ||
      style.position === "absolute"
    ) {
      return false;
    }
    const rect = child.getBoundingClientRect();
    if (rect.width <= 0) return false;
    if (rect.height > 0) return true;
    return (
      child.childElementCount > 0 &&
      style.overflowX === "visible" &&
      style.overflowY === "visible"
    );
  });

// Empty space taller than a button bar is a column stretched to the iframe.
// Smaller gaps are the button's own box, which has to stay in the height or
// the iframe clips the buttons.
const STRETCH_GAP = 48;

const intrinsicBottom = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  const children = inFlowChildren(element);

  if (!children.length) {
    const isStretchedMedia =
      (element instanceof HTMLImageElement ||
        element instanceof HTMLVideoElement) &&
      style.height === "100%" &&
      "naturalWidth" in element &&
      element.naturalWidth > 0;
    if (isStretchedMedia) {
      const naturalHeight =
        (element.naturalHeight / element.naturalWidth) * rect.width;
      return rect.top + Math.min(rect.height, naturalHeight);
    }
    return rect.bottom;
  }

  const childEdge = Math.max(
    ...children.map((child) => intrinsicBottom(child))
  );
  const tail =
    (parseFloat(style.paddingBottom) || 0) +
    (parseFloat(style.borderBottomWidth) || 0);
  // Gemstone sections pad 8px under Product Details. That padding is not a
  // child, so stopping at the last child leaves the iframe short and the page
  // scrolls by those few pixels. A column stretched to the iframe is ignored.
  if (rect.bottom > childEdge + tail + STRETCH_GAP) return childEdge + tail;
  return Math.max(rect.bottom, childEdge);
};

// Document height of real content. A box stretched to the iframe is ignored;
// padding below the last child is kept.
const contentEdge = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  const children = inFlowChildren(element);
  const tail =
    (parseFloat(style.paddingBottom) || 0) +
    (parseFloat(style.borderBottomWidth) || 0);

  if (!children.length) return rect.bottom;

  const childEdge = Math.max(...children.map((child) => contentEdge(child)));
  if (rect.bottom > childEdge + tail + STRETCH_GAP) return childEdge + tail;
  return Math.max(rect.bottom, childEdge);
};

export const measureElementDocumentHeight = (element: HTMLElement) => {
  const marginBottom =
    parseFloat(window.getComputedStyle(element).marginBottom) || 0;
  return Math.ceil(contentEdge(element) + window.scrollY + marginBottom);
};

const findPageScroller = (): HTMLElement | null => {
  let pageScroller: HTMLElement | null = null;
  document.body?.querySelectorAll<HTMLElement>("div").forEach((scroller) => {
    if (scroller.clientHeight < window.innerHeight * 0.4) return;
    const style = window.getComputedStyle(scroller);
    if (style.overflowY !== "auto" && style.overflowY !== "scroll") return;
    if (style.visibility === "hidden" || style.display === "none") return;
    if (isInInactiveScreen(scroller) || isInsideFixedLayer(scroller)) return;
    if (scroller.getBoundingClientRect().width === 0) return;
    if (!pageScroller || scroller.contains(pageScroller)) {
      pageScroller = scroller;
      return;
    }
    if (pageScroller.contains(scroller)) return;
    if (scroller.clientHeight > pageScroller.clientHeight) {
      pageScroller = scroller;
    }
  });
  return pageScroller;
};

// Content bottom inside the active screen. This is not document scrollHeight:
// a column stretched to the iframe does not extend the page.
export const measureActiveContentHeight = () => {
  const scroller = findPageScroller();
  if (!scroller) return 0;
  const scrollerRect = scroller.getBoundingClientRect();
  const visibleBottom = scrollerRect.top + scroller.clientHeight;
  const contentBottom = intrinsicBottom(scroller);
  if (contentBottom <= scrollerRect.top + 1) return 0;
  const chromeBelow = Math.max(0, window.innerHeight - visibleBottom);
  return Math.ceil(contentBottom + chromeBelow);
};

export const isInactiveFrame = isInInactiveScreen;
